/**
 * Batch-resolve Page slugs for Blog entries (linkedFrom.pageCollection).
 * Used by CF-050 when Blog.url is not configured in Contentful.
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {getBlogPageSlugsByBlogIds} from '@amblique/king-living/app/contentful/queries/blog-page-slugs.queries';
import {buildBlogPageSlugMap} from '@amblique/king-living/app/contentful/utils/format-blog-page-slug';

/**
 * @param {string[]} blogIds - Blog sys.id values needing a page slug fallback
 * @param {Object} [options={}]
 * @param {import('@tanstack/react-query').UseQueryOptions<Map<string, string>, Error>} [options.queryOptions={}]
 * @returns {import('@tanstack/react-query').UseQueryResult<Map<string, string>, Error>}
 */
export function useBlogPageSlugsByIds(blogIds = [], options = {}) {
  const contentfulService = useService(ContentfulService);

  const normalizedIds = [...new Set(blogIds.map((id) => String(id || '').trim()).filter(Boolean))];
  const {queryOptions = {}} = options;
  const {enabled: enabledFromOptions, ...restQueryOptions} = queryOptions;

  const resolvedEnabled =
    !!contentfulService &&
    normalizedIds.length > 0 &&
    (typeof enabledFromOptions === 'boolean' ? enabledFromOptions : true);

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'blogPageSlugs',
      contentfulService?.locale,
      normalizedIds.join(','),
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(getBlogPageSlugsByBlogIds),
        {
          locale: contentfulService.locale,
          preview: false,
          blogIds: normalizedIds,
        }
      );

      return buildBlogPageSlugMap(data?.blogCollection?.items || []);
    },

    enabled: resolvedEnabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...restQueryOptions,
  });
}
